import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { ThemeOptions, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DialogSummary from '@/components/DialogSummary/DialogSummary';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ArrayChipLogic from '@/components/UI/ArrayChips/ArrayChipLogic';
import Calendar from '@/components/UI/Calendar/Calendar';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import DisclaimerCheckbox from '@/components/UI/DisclaimerCheckbox/DisclaimerCheckbox';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import InputText from '@/components/UI/InputText/InputText';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import {
  statusCampaign, LONG_FULL_DATE_FORMAT, DOORMAN_PROCESS_KEY, HELP_AREA,
  errorTypes, INFORMER_CHECK_APPROVER_CR, CAUSER_CHECK_APPROVER_CR, CAUSER_PRE_APPROVER_CR, INFORMER_PRE_APPROVER_CR,
  INFORMER_APPROVER_CR, CAUSER_APPROVER_CR, isNotPRO, PDRS, DESCRIPTION, CLASSIFICATION, TAB_FORMS_OPTIONS,
  AFF_VEHICLES, classificationSelectOptions,
} from '@/helpers/constants';
import { getCampaignId, getFullFormattedDate, isSubCR, stringToDate } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useError from '@/hooks/useError';
import { setCampaignSummary, setCampaignRecipients } from '@/redux/Campaign/campaign.action';
import {
  getCampaignInfo, getCampaingSummary, getCampaignRecipients, getIsVANCR,
} from '@/redux/Campaign/campaign.reducer';
import { setDescriptionForms, setClassificationForms, setAffectedVehiclesForms } from '@/redux/Forms/forms.action';
import { getDescriptionForms, getClassificationForms, getAffectedVehiclesForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { setSubCampaignSummary } from '@/redux/SubCampaign/subCampaign.action';
import { getSubCampaignSummary } from '@/redux/SubCampaign/subCampaign.reducer';
import { setUserPreference } from '@/redux/User/user.action';
import { getSettingsInfo, getUserPreferences } from '@/redux/User/user.reducer';
import { getWorkflow } from '@/redux/Workflow/workflow.reducer';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import {
  CampaignInfo, LangTextMandatory, Recipients, ReduxForms, Summary as SummaryType,
  SummaryUserPreferences, UserSettings, Workflow,
} from '@/types';
import Image from './Image/Image';
import MultipleSectionInput from './MultipleSectionInput/MultipleSectionInput';
import defaultSummary from './Summary.utils';
import styles from './Summary.module.scss';

type PossibleCauserFieldConfig = { key: string; label: string; extraKey?: string };

const POSSIBLE_CAUSER_INTERNAL_FIELDS: PossibleCauserFieldConfig[] = [
  { key: DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.CAUSER, label: 'summary.contactPerson' },
  { key: DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.COST_CENTER, label: 'summary.costCenter' },
];

const POSSIBLE_CAUSER_EXTERNAL_FIELDS: PossibleCauserFieldConfig[] = [
  { key: DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.CONTACT_PERSON, label: 'summary.internalContactPerson' },
  { key: DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.SUPPLIER_NUMBER, label: 'summary.supplier', extraKey: DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.SUPPLIER_NAME },
  { key: DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_NUMBER, label: 'summary.tier1Supplier', extraKey: DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_NAME },
];

const POSSIBLE_CAUSER_EXTERNAL = 'External';

const useStyles = makeStyles((theme: ThemeOptions & Theme) => ({
  root: {
    width: '100%',
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

type SummaryProps = {
  headerLanguage: string;
  summary: SummaryType;
  setSummaryDataRedux: (data: SummaryType) => void;
  summarySubCR: SummaryType;
  setSubCRSummary: (data: any) => void;
  campaignInfo: CampaignInfo;
  setPreferences: (data: SummaryUserPreferences) => void;
  userPreferences: SummaryUserPreferences;
  descriptionForms: ReduxForms;
  setDescriptForms: (data: ReduxForms) => void;
  affVehiclesForms: ReduxForms;
  setAffVehiclesForms: (data: ReduxForms) => void;
  formsClassification: ReduxForms;
  setFormsClassification: (data: ReduxForms) => void;
  workflow: Workflow;
  setHelpArea: (tab: string) => void;
  setRecipients: (data: Recipients) => void;
  recipients: Recipients;
  settingsInfo: UserSettings;
  isVANCR: boolean;
};

const Summary: React.FC<SummaryProps> = (props) => {
  const { headerLanguage, summary, setSummaryDataRedux, summarySubCR, setSubCRSummary, campaignInfo,
    setPreferences, userPreferences, descriptionForms, setDescriptForms, affVehiclesForms, setAffVehiclesForms, formsClassification, setFormsClassification,
    workflow, setHelpArea, setRecipients, recipients, settingsInfo, isVANCR } = props;

  const { id: paramId, version } = useParams<{ id: string; version: string }>();
  const classes = useStyles();
  const authService = useAuth();
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [image, setImage] = useState({
    src: '',
    id: '',
    name: '',
    type: '',
  });
  const [affectedVehicles, setAffectedVehicles] = useState<[string, string[]][]>();
  const [referenceSystem, setReferenceSystem] = useState('');
  const [triggerFieldValue, setTriggerFieldValue] = useState('');
  const [labelOtherName, setLabelOtherName] = useState('');
  const { handleCriticalError } = useError();
  const [openCampaignRequestDetails, setOpenCampaignRequestDetails] = useState(false);
  const [formsValues, setformsValues] = useState<undefined | { options: { [formKey: string]: LangTextMandatory } }>();
  const [openChipsSeriesDetails, setOpenChipsSeriesDetails] = useState(false);
  const [chipSelection, setChipSelection] = useState<{ plant: string; series: string[] }>();
  const [otherInformationList, setOtherInformationList] = useState<string[] | undefined>();
  const [summaryMessage, setSummaryMessage] = useState<{ text: string; action: string }>({ text: '', action: '' });
  const setSafeSummaryMessage = (msg: { text: string; action: string }) => {
    if (summaryMessage.text !== msg.text || summaryMessage.action !== msg.action) {
      setSummaryMessage(msg);
    }
  };
  const date = getFullFormattedDate(summary?.statusDate, settingsInfo.timezone, LONG_FULL_DATE_FORMAT);
  const id = getCampaignId(paramId);
  const isExternal = summary?.possibleCauserSelected === POSSIBLE_CAUSER_EXTERNAL;
  const isLoading = !summary?.id;
  const setSummaryData = (completeSummary: SummaryType) => {
    const { attachment, affectedVehiclesByPlants, attachmentId, attachmentName, attachmentType } = completeSummary;
    setImage({
      src: attachment,
      id: attachmentId,
      name: attachmentName,
      type: attachmentType,
    });
    setSummaryDataRedux(completeSummary);
    setAffectedVehicles(Object.entries(affectedVehiclesByPlants || {}));
  };

  const isRejected = workflow?.statuses?.map(({ actionTaken }) => actionTaken).includes('REJECTED');

  const translate = (summaryField = {}) => summaryField[headerLanguage] || '';

  const saveCampaignRequestDetails = (newPreference) => {
    usersServices.saveUsersSettingsPreferences(authService.userCode, newPreference).then(() => setOpenCampaignRequestDetails(false));
  };

  const InfoMsgStatusE2 = () => {
    if (summary?.status === statusCampaign.IN_CAUSER_APPROVAL) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_APPROVAL);

      if (!listHasInformar) {
        setSafeSummaryMessage({ text: t('summary.scenaryE2Oneway'), action: '' });
      }
    }

    if (summary?.status === statusCampaign.IN_INFORMER_APPROVAL) {
      setSafeSummaryMessage({ text: t('summary.scenaryE2TwoWay'), action: '' });
    }

    if (summary?.status === statusCampaign.IN_CAUSER_APPROVAL) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_APPROVAL);

      if (listHasInformar) {
        setSafeSummaryMessage({ text: t('summary.scenaryE2TwoWayInfomerApproved'), action: '' });
      }
    }

    if (isRejected) {
      if (summary?.statusUserRole === INFORMER_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE2I', { date, comment: summary?.statusComment }), action: 'Warning' });
      }

      if (summary?.statusUserRole === CAUSER_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE2C', { date, comment: summary?.statusComment }), action: 'Warning' });
      }
    }
  };

  const InfoMsgStatusE3 = () => {
    if (summary?.status === statusCampaign.IN_CAUSER_PRE_APPROVAL) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_PRE_APPROVAL);

      if (!listHasInformar) {
        setSafeSummaryMessage({ text: t('summary.scenaryE3Oneway'), action: '' });
      }
    }

    if (summary?.status === statusCampaign.IN_INFORMER_PRE_APPROVAL) {
      setSafeSummaryMessage({ text: t('summary.scenaryE3TwoWay'), action: '' });
    }

    if (summary?.status === statusCampaign.IN_CAUSER_PRE_APPROVAL) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_PRE_APPROVAL);

      if (listHasInformar) {
        setSafeSummaryMessage({ text: t('summary.scenaryE3TwoWayInfomerApproved'), action: '' });
      }
    }

    if (isRejected) {
      if (summary?.statusUserRole === INFORMER_PRE_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE3I', { date, comment: summary?.statusComment }), action: 'Warning' });
      }

      if (summary?.statusUserRole === CAUSER_PRE_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE3C', { date, comment: summary?.statusComment }), action: 'Warning' });
      }
    }
  };

  const InfoMsgStatusE4 = () => {
    if (summary?.status === statusCampaign.IN_CAUSER_CHECK) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_CHECK);

      if (!listHasInformar) {
        setSafeSummaryMessage({ text: t('summary.scenaryE4Oneway'), action: '' });
      }
    }

    if (summary?.status === statusCampaign.IN_INFORMER_CHECK) {
      setSafeSummaryMessage({ text: t('summary.scenaryE4TwoWay'), action: '' });
    }

    if (summary?.status === statusCampaign.IN_CAUSER_CHECK) {
      const listHasInformar = summary?.successfulStatuses.some((list) => list.status === statusCampaign.IN_INFORMER_CHECK);

      if (listHasInformar) {
        setSafeSummaryMessage({ text: t('summary?.scenaryE4TwoWayInfomerApproved'), action: '' });
      }
    }

    if (isRejected) {
      if (summary.statusUserRole === INFORMER_CHECK_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE4I', { date, comment: summary?.statusComment }), action: 'Warning' });
      }

      if (summary?.statusUserRole === CAUSER_CHECK_APPROVER_CR) {
        setSafeSummaryMessage({ text: t('summary.rejectedFromE4C', { date, comment: summary?.statusComment }), action: 'Warning' });
      }
    }
  };

  const manageInfoMsg = () => {
    if (summary && summary?.status) {
      if (([statusCampaign.IN_CAUSER_APPROVAL, statusCampaign.IN_INFORMER_APPROVAL] as string[]).includes(summary?.status)
        || (summary?.status === statusCampaign.CLOSED && summary?.statusUserRole.includes('E2'))) {
        InfoMsgStatusE2();
      }

      if (([statusCampaign.IN_CAUSER_PRE_APPROVAL, statusCampaign.IN_INFORMER_PRE_APPROVAL] as string[]).includes(summary?.status)
        || (summary?.status === statusCampaign.CLOSED && summary?.statusUserRole.includes('E3'))) {
        InfoMsgStatusE3();
      }

      if (([statusCampaign.IN_CAUSER_CHECK, statusCampaign.IN_INFORMER_CHECK] as string[]).includes(summary?.status)
        || (summary?.status === statusCampaign.CLOSED && summary?.statusUserRole.includes('E4'))) {
        InfoMsgStatusE4();
      }
    }
  };

  const listOtherInformations = (originalForms, userSettings) => {
    setOtherInformationList(Object.keys(originalForms?.options || {})
      .filter((forms) => !userSettings?.executiveSummaryPreferences?.includes(forms)));
  };

  useEffect(() => {
    manageInfoMsg();
  }, [summary?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => manageInfoMsg(), [settingsInfo.timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!formsClassification.classification) {
      campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_CLASSIFICATION).then((res) => {
        setFormsClassification(res.form);
      }).catch(handleCriticalError);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!recipients?.id && !isSubCR()) {
      campaignServices.getCampaignRetry(id, 'recipients', version, campaignInfo.type, campaignInfo.creationDate)
        .then(setRecipients)
        .catch(handleCriticalError);
    }
  }, [settingsInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCampaign = useApiRequest(campaignServices.getCampaign);

  useEffect(() => {
    if (isSubCR()) {
      campaignServices.getSubCampaign(paramId, 'summary')
        .then((result) => {
          setSubCRSummary(result);
          setTriggerFieldValue(descriptionForms?.source?.options[result?.triggerSource]?.[lang] || '');
        })
        .catch((err) => {
          setSubCRSummary({});
          handleCriticalError(err);
        });
    } else {
      fetchCampaign(id, 'summary', version)
        .then((result) => {
          if (!result) return;
          setSummaryData(result);
          setTriggerFieldValue(descriptionForms?.source?.options[result?.triggerSource]?.[lang] || '');
        })
        .catch((err) => {
          if (err.name !== errorTypes.ABORT_ERROR) {
            setSummaryData(defaultSummary(id));
            handleCriticalError(err);
          }
        });
    }
    setTriggerFieldValue(descriptionForms?.source?.options[summary?.triggerSource]?.[lang] || '');
  }, [id, campaignInfo?.status, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ira = summary?.iraNumber === '' ? '' : `${t('involvedParties.ira', { lng: lang.toLowerCase() })}-${summary?.iraNumber}`;

    if (summary?.triggerSource === DOORMAN_PROCESS_KEY) {
      setReferenceSystem(`${t('involvedParties.doormanRequestID')} ${summary?.iraNumber}`);
    } else if (['SQMS', 'CAT', 'SAP', 'ZEUS', 'SBM', 'STARC_GENERATED'].includes(summary?.referencedSystem)) {
      setReferenceSystem(ira);
    } else if (summary?.referencedSystem === 'OTHER') {
      setReferenceSystem(ira);
      setLabelOtherName(summary?.referencedSystemName);
    } else {
      setReferenceSystem(summary?.referencedSystem);
    }
  }, [summary?.referencedSystem, summary?.iraNumber, summary?.triggerSource, summary?.referencedSystemName, t, lang]);

  useEffect(() => {
    Promise.all([
      campaignServices.getSummaryForms(),
      usersServices.getUsersSettingsPreferences(authService.userCode),
    ]).then((res) => {
      const [summaryResponse, settings] = res;
      setformsValues(summaryResponse.form.ProblemOverview);
      setPreferences(settings);
      listOtherInformations(summaryResponse.form.ProblemOverview, settings);
    }).catch(handleCriticalError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    listOtherInformations(formsValues, userPreferences);
  }, [userPreferences?.executiveSummaryPreferences, formsValues]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const setFormsInfo = async () => {
      try {
        const { form: descriptionForm } = await campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_DESCRIPTION, campaignInfo.creationDate);
        const { form: affVehiclesForm } = await campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_AFF_VEHICLES, campaignInfo.creationDate);
        setDescriptForms(descriptionForm);
        setAffVehiclesForms(affVehiclesForm);
      } catch (error) {
        handleCriticalError(error);
      }
    };

    if (campaignInfo?.creationDate) {
      setFormsInfo();
    }
  }, [campaignInfo?.creationDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const mixedLabel = (label1, label2, value) => (
    <>
      {`${t(label1)} | ${t(label2)}: `}
      <TooltipComponent title={value}>
        <strong className={styles.ClassLabelValue}>{value}</strong>
      </TooltipComponent>
    </>
  );

  const transformResult = (text, forms) => {
    if (text && forms) {
      const textArray = text.split(',').map((cls) => cls.trim());
      const arrayText = textArray.map((option) => forms[option]?.[lang]).filter((op) => op);
      const data = arrayText.join(', ');

      return data;
    }

    return [];
  };

  const updateValue = (forms) => {
    const checkIsValue = Object.entries(summary).find((option) => option[0] === forms);

    if (forms === 'blockingRecommendation' && checkIsValue && checkIsValue[1] === 'YES') {
      return t('dialog.yes');
    }

    if (forms === 'blockingRecommendation' && checkIsValue && checkIsValue[1] === 'NO') {
      return t('dialog.no');
    }

    if (forms === 'blockingRecommendation' && checkIsValue && checkIsValue[1] === 'EMPTY') {
      return '';
    }

    if (forms === 'classificationSelected') {
      const srcValues = [...classificationSelectOptions, 'Sicherheit', 'Ordnungsmäßigkeit', 'pot. Zertifizierung'];
      const isInSrc = srcValues.some((value) => (summary?.classificationSelected?.includes(value)));

      return transformResult(summary?.classificationSelected, formsClassification?.[isInSrc ? 'classification' : 'customerRelevancy']?.options);
    }

    if (forms === AFF_VEHICLES.AMG) {
      return transformResult(summary?.amgVehicles, affVehiclesForms?.amgVehicles?.options);
    }

    return checkIsValue ? checkIsValue[1] : '';
  };

  const updateLabel = (forms) => {
    const checkisLabel = Object.entries(formsValues?.options || {}).find((option) => option[0] === forms);

    return checkisLabel ? checkisLabel[1][lang] : '';
  };

  const labelSrc = () => {
    const srcValues = [...classificationSelectOptions, 'Sicherheit', 'Ordnungsmäßigkeit', 'pot. Zertifizierung'];
    const isInSrc = srcValues.some((value) => (summary?.classificationSelected?.includes(value)));
    const selected = isInSrc ? t('summary.SCERelevance') : t('classification.customerRelevancy');

    const textWithoutWhiteSpaces = summary?.classificationSelected;
    const textArray = textWithoutWhiteSpaces?.split(',');
    const arrayText = textArray?.map((option) => {
      const optionTrim = option.trim();
      const isInSrcvalue = srcValues.includes(optionTrim);

      return formsClassification?.[isInSrcvalue ? 'classification' : 'customerRelevancy']?.options[optionTrim]?.[lang];
    });

    const data = arrayText?.join(', ');

    return mixedLabel('summary.classification', selected, data);
  };

  const possibleCauserValue = () => {
    const fields = isExternal ? POSSIBLE_CAUSER_EXTERNAL_FIELDS : POSSIBLE_CAUSER_INTERNAL_FIELDS;
    const hasData = (value) => value !== undefined && value !== null && `${value}`.trim() !== '';

    return fields
      .filter(({ key, extraKey }) => hasData(summary?.[key]) || (extraKey && hasData(summary?.[extraKey])))
      .map(({ key, label, extraKey }) => {
        const value = extraKey
          ? [summary?.[key], summary?.[extraKey]].filter(hasData).join(',\n')
          : summary?.[key];

        return `${t(label)}:\n${value}`;
      })
      .join('\n\n');
  };

  const affectedVehiclesOrProducedVehicle = summary?.typeOfAffectedPlantsSelection === 'SERIES' ? 'summary.affectedVehicles' : 'summary.producedVehicles';

  const openSeriesDialog = (series, plant) => {
    const newState = { series, plant };
    setChipSelection(newState);
    setOpenChipsSeriesDetails(true);
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <div className={styles.Summary}>
          <div className={classes.root}>
            {summaryMessage.text && <InfoMsg msg={summaryMessage.text} action={summaryMessage?.action} />}
          </div>
          <AccordionPanel title={t('summary.title')} handleClick={() => setHelpArea(HELP_AREA.PROBLEM_OVERVIEW)}>
            <div className="w-100">
              {userPreferences?.executiveSummaryPreferences && formsValues && (
                <div className={styles.Preferences}>
                  {Object.values(userPreferences?.executiveSummaryPreferences).map((forms) => (
                    <InputText
                      disabled
                      id={`summary-userspreference-${forms}`}
                      key={forms}
                      label={updateLabel(forms)}
                      formControlClass={styles.InputSize}
                      value={updateValue(forms)}
                      multiline={updateValue(forms)?.length > 20}
                    />
                  ))}
                </div>
              )}
              <div className={styles.Preferences}>
                <div className={styles.PreferencesButton}>
                  <SimpleButton
                    id="button-edit-summary-preferences"
                    color="primary"
                    buttonText={t('summary.edit')}
                    handleClick={() => setOpenCampaignRequestDetails(true)}
                  />
                  {openCampaignRequestDetails && (
                    <DialogSummary
                      close={() => setOpenCampaignRequestDetails(false)}
                      confirm={saveCampaignRequestDetails}
                      formsValues={formsValues as unknown as ReduxForms}
                    />
                  )}
                </div>
              </div>
              <h3>{t('description.label')}</h3>
              <div className={styles.ProblemOverview}>
                <Image src={image.src} id={image.id} name={image.name} type={image.type} className={styles.ContainerImg} />
                <InputText
                  disabled
                  multiline
                  maxLength={512}
                  rows={5}
                  id="input-executive-problem-description"
                  label={t('summary.executiveProblemDescription')}
                  value={translate(summary.executiveProblemDescription)}
                />
                <InputText
                  disabled
                  multiline
                  maxLength={512}
                  rows={5}
                  id="input-root-cause"
                  label={t('summary.rootCause')}
                  value={translate(summary.rootCause)}
                />
                <MultipleSectionInput
                  disabled
                  id="input-inspection-immediate-measure"
                  label={mixedLabel('summary.immediateMeasure', 'summary.inspectionType',
                    transformResult(summary.inspectionType, descriptionForms?.inspectionType?.options))}
                  className={styles.MultipleSectionInput}
                  firstSectionLabel={`${t('summary.inspection')} :`}
                  firstSectionValue={translate(summary.inspectionTypeComment)}
                  secondSectionLabel={`${t('summary.immediateMeasure')} :`}
                  secondSectionValue={translate(summary.immediateMeasureComment)}
                />
                <InputText
                  disabled
                  multiline
                  maxLength={512}
                  rows={5}
                  id="input-consequences"
                  label={t('summary.consequences')}
                  value={translate(summary.consequences)}
                />
              </div>
            </div>
          </AccordionPanel>
          <AccordionPanel title={t(affectedVehiclesOrProducedVehicle)} handleClick={() => setHelpArea(HELP_AREA.AFFECTED_VEHICLES)}>
            <div className="w-100">
              <div className={styles.Plants}>
                {(isSubCR() ? Object.entries(summarySubCR.affectedVehiclesByPlants || {}) : affectedVehicles || [])
                  .filter(([plant]) => plant !== 'otherPlantNames')
                  .map(([plant, series]) => (
                    <div key={`${plant}-${series}`} className={styles.InputPlants}>
                      <div className={styles.Label}>{plant}</div>
                      <ArrayChipLogic id={`chip-${plant}`} chipData={series as string[]} onOpenDetails={() => openSeriesDialog(series, plant)} />
                    </div>
                  ))}
              </div>
              {(isSubCR() && summarySubCR.affectedVehiclesByPlants?.otherPlantNames.length > 0) && (
                <>
                  <h3>{t('summary.otherAffectedPlants')}</h3>
                  <ArrayChipLogic
                    id="chip-other-affected-plants"
                    chipData={summarySubCR.affectedVehiclesByPlants?.otherPlantNames}
                    maxChip={99}
                  />
                </>
              )}
              {(openChipsSeriesDetails && chipSelection) && (
                <ConfirmDialog
                  reject={() => setOpenChipsSeriesDetails(false)}
                  title={chipSelection?.plant}
                >
                  <div className={styles.BoxContent}>
                    {chipSelection?.series?.map((serie) => (
                      <div key={serie} className={styles.BoxShips}>{serie}</div>
                    ))}
                  </div>
                </ConfirmDialog>
              )}
              <h3>{t('summary.otherInformation')}</h3>
              <div className={styles.OtherInformation}>
                <InputText
                  disabled
                  className={styles.Classification}
                  multiline
                  maxLength={256}
                  rows={5}
                  id="input-classification"
                  label={<TooltipComponent title={labelSrc()}>{labelSrc()}</TooltipComponent>}
                  value={translate(summary.classificationComment)}
                />
                <InputText
                  disabled
                  className={styles.PossibleCauser}
                  multiline
                  maxLength={256}
                  rows={5}
                  id="input-possible-causer"
                  label={(
                    <>
                      {`${t('summary.possibleCauser')} | `}
                      <strong className={styles.ClassLabelValue}>{summary.possibleCauserSelected}</strong>
                    </>
                  )}
                  value={possibleCauserValue()}
                />
                <div className={styles.OneLineInputs}>
                  <InputText
                    disabled
                    id="input-trigger-source"
                    label={mixedLabel('summary.triggerSource', 'summary.failureOn', summary.triggerFailureOn)}
                    value={triggerFieldValue}
                  />
                  <InputText
                    disabled
                    id="input-reference-system"
                    label={(
                      <>
                        {`${t('summary.referenceSystem')} | `}
                        {summary.referencedSystem === 'OTHER'
                          ? <strong className={styles.ClassLabelValue}>{labelOtherName}</strong>
                          : <strong className={styles.ClassLabelValue}>{summary.referencedSystem?.replaceAll('_GENERATED', '')}</strong>}
                      </>
                    )}
                    value={referenceSystem || ''}
                  />
                </div>

                {isExternal && (
                  <DisclaimerCheckbox
                    containerStyle={styles.Tier1SupplierNote}
                    id="tier1-supplier-disclaimer"
                    disabled
                    checked={summary.possibleCauserTier1SupplierAcknowledged}
                    disclaimerHeaderText="description.tier1SupplierAcknowledgement.title"
                    message="description.tier1SupplierAcknowledgement.message"
                  />
                )}

                {!isExternal && (
                  <DisclaimerCheckbox
                    containerStyle={styles.Tier1SupplierNote}
                    id="confirm-plant-to-plant"
                    disabled
                    checked={summary.possibleCauserConfirmPlantToPlant}
                    message="involvedParties.confirmPlanToPlant"
                  />
                )}

                {isNotPRO && !isVANCR && triggerFieldValue === PDRS && (
                  <InputText
                    id="buildUpApproval"
                    disabled={true}
                    fieldPath={['description', DESCRIPTION.BUILD_UP_APPROVAL]}
                    className={styles.BuildUp}
                    multiline
                    maxLength={256}
                    rows={5}
                    label={t('description.buildUpApproval')}
                    value={translate(summary.triggerBuildUpApproval)}
                  />
                )}
              </div>
              <h3>{t('classification.consultation')}</h3>
              <div className={styles.Consultation}>
                <InputText
                  disabled
                  id="consultation-type"
                  label={t('classification.consultationType')}
                  value={summary?.consultationType || ''}
                  fieldPath={['classification', CLASSIFICATION.CONSULTATION_TYPE]}
                />
                <div className={styles.ConsultationRow}>
                  <Calendar
                    disabled
                    fieldPath={['classification', CLASSIFICATION.CONSULTATION_DATE]}
                    formControlClass={styles.ConsultationDateWrapper}
                    id="date-picker-consultation-date"
                    label={t('classification.consultationDate')}
                    margin="none"
                    value={stringToDate(summary.consultationDate)}
                  />
                  <InputText
                    fieldPath={['classification', CLASSIFICATION.CONSULTATION_NAME]}
                    userFieldPath={['classification', CLASSIFICATION.CONSULTATION_NAME]}
                    disabled
                    id="input-consultant-name"
                    label={t('classification.consultantName')}
                    value={summary?.consultantName?.summary || ''}
                  />
                </div>
                <InputText
                  disabled
                  multiline
                  fieldPath={['classification', CLASSIFICATION.CONSULTATION_COMMENT]}
                  maxLength={256}
                  rows={5}
                  id="input-consultation-comment"
                  label={t('classification.consultationComment')}
                  value={summary?.consultationComment?.[headerLanguage] || ''}
                />
              </div>
              <h3>{t('affectedVehicles.technicalAssessmentOfTheTopic')}</h3>
              {otherInformationList && (
                <div className={styles.Preferences}>
                  {Object.values(otherInformationList).map((forms) => (
                    <InputText
                      disabled
                      id={`summary-otherInformation-${forms}`}
                      key={updateLabel(forms)}
                      label={updateLabel(forms)}
                      formControlClass={styles.InputSize}
                      value={updateValue(forms) || ''}
                    />
                  ))}
                </div>
              )}
            </div>
          </AccordionPanel>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  summary: isSubCR() ? (getSubCampaignSummary(state)?.module || {}) : getCampaingSummary(state),
  summarySubCR: getSubCampaignSummary(state)?.module || {},
  descriptionForms: getDescriptionForms(state),
  affVehiclesForms: getAffectedVehiclesForms(state),
  formsClassification: getClassificationForms(state),
  campaignInfo: getCampaignInfo(state),
  workflow: getWorkflow(state),
  userPreferences: getUserPreferences(state),
  recipients: getCampaignRecipients(state),
  settingsInfo: getSettingsInfo(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setDescriptForms: setDescriptionForms,
  setAffVehiclesForms: setAffectedVehiclesForms,
  setFormsClassification: setClassificationForms,
  setSummaryDataRedux: setCampaignSummary,
  setSubCRSummary: setSubCampaignSummary,
  setPreferences: setUserPreference,
  setHelpArea: setAreaHelp,
  setRecipients: setCampaignRecipients,
};

export default (connect(mapStateToProps, actionCreators)(Summary));
